import { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import { Message } from 'primereact/message';

const WorkExperienceStep = ({ data, onUpdate }) => {
    const [salariedExperiences, setSalariedExperiences] = useState(data.salaried_experiences || []);
    const [selfEmployedExperiences, setSelfEmployedExperiences] = useState(data.self_employed_experiences || []);

    // Get selected employment types from basic info
    const selectedEmploymentTypes = Array.isArray(data.employment_types) ? data.employment_types : [];
    const showSalaried = selectedEmploymentTypes.includes('salaried');
    const showSelfEmployed = selectedEmploymentTypes.includes('self_employed');

    // Legacy work_experiences support (for backward compatibility)
    useEffect(() => {
        if (data.work_experiences && data.work_experiences.length > 0 && salariedExperiences.length === 0) {
            setSalariedExperiences(data.work_experiences);
        }
    }, [data.work_experiences, salariedExperiences.length]);

    const employmentTypes = [
        { label: 'Full Time', value: 'full_time' },
        { label: 'Part Time', value: 'part_time' },
        { label: 'Contract', value: 'contract' },
        { label: 'Freelance', value: 'freelance' }
    ];

    const businessTypes = [
        { label: 'Sole Proprietor', value: 'sole_proprietor' },
        { label: 'Partnership', value: 'partnership' },
        { label: 'LLP', value: 'llp' },
        { label: 'Private Limited', value: 'pvt_ltd' },
        { label: 'Public Limited', value: 'public_ltd' },
    ];

    // Salaried Experience Functions
    const addSalariedExperience = () => {
        const newWork = {
            company_name: '',
            job_title: '',
            employment_type: 'full_time',
            location: '',
            description: '',
            start_date: null,
            end_date: null,
            is_current: false
        };

        const updated = [...salariedExperiences, newWork];
        setSalariedExperiences(updated);
        onUpdate({
            salaried_experiences: updated,
            work_experiences: updated // For backward compatibility
        });
    };

    const removeSalariedExperience = (index) => {
        const updated = salariedExperiences.filter((_, i) => i !== index);
        setSalariedExperiences(updated);
        onUpdate({
            salaried_experiences: updated,
            work_experiences: updated // For backward compatibility
        });
    };

    const updateSalariedExperience = (index, field, value) => {
        const updated = [...salariedExperiences];
        updated[index] = { ...updated[index], [field]: value };

        // If marking as current, clear end_date
        if (field === 'is_current' && value === true) {
            updated[index].end_date = null;
        }

        setSalariedExperiences(updated);
        onUpdate({
            salaried_experiences: updated,
            work_experiences: updated // For backward compatibility
        });
    };

    // Self-Employed Experience Functions
    const addSelfEmployedExperience = () => {
        const newBusiness = {
            business_name: '',
            role_title: '',
            business_type: 'sole_proprietor',
            location: '',
            description: '',
            start_date: null,
            end_date: null,
            is_current_running: false
        };

        const updated = [...selfEmployedExperiences, newBusiness];
        setSelfEmployedExperiences(updated);
        onUpdate({ self_employed_experiences: updated });
    };

    const removeSelfEmployedExperience = (index) => {
        const updated = selfEmployedExperiences.filter((_, i) => i !== index);
        setSelfEmployedExperiences(updated);
        onUpdate({ self_employed_experiences: updated });
    };

    const updateSelfEmployedExperience = (index, field, value) => {
        const updated = [...selfEmployedExperiences];
        updated[index] = { ...updated[index], [field]: value };

        // If marking as currently running, clear end_date
        if (field === 'is_current_running' && value === true) {
            updated[index].end_date = null;
        }

        setSelfEmployedExperiences(updated);
        onUpdate({ self_employed_experiences: updated });
    };

    // Show message if no employment type selected
    if (!showSalaried && !showSelfEmployed) {
        return (
            <div className="grid">
                <div className="col-12">
                    <Message
                        severity="info"
                        text="Please select at least one Employment Type (Salaried or Self-Employed) in the Basic Information step to add work experience."
                        className="w-full"
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="grid">
            <div className="col-12">
                <h5 className="mt-0 mb-1">Work Experience</h5>
                <p className="text-600 mt-0 mb-4">Add your professional work history based on your employment type</p>
            </div>

            {/* Salaried Experience Section */}
            {showSalaried && (
                <>
                    <div className="col-12">
                        <Divider align="left">
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-briefcase text-blue-500"></i>
                                <span className="font-semibold text-lg">Salaried Employment</span>
                            </div>
                        </Divider>
                    </div>

                    <div className="col-12">
                        <div className="flex justify-content-between align-items-center mb-3">
                            <p className="text-600 m-0">Add your employment history as a salaried employee</p>
                            <Button
                                label="Add Experience"
                                icon="pi pi-plus"
                                onClick={addSalariedExperience}
                                className="p-button-outlined p-button-sm"
                            />
                        </div>
                    </div>

                    {salariedExperiences.length === 0 && (
                        <div className="col-12">
                            <div className="surface-100 border-round p-4 text-center">
                                <i className="pi pi-briefcase text-4xl text-400 mb-3"></i>
                                <p className="text-600 m-0">No salaried work experience added yet</p>
                                <p className="text-500 text-sm m-0">Click "Add Salaried Experience" to get started</p>
                            </div>
                        </div>
                    )}

                    {salariedExperiences.map((work, index) => (
                        <div key={index} className="col-12">
                            <Card className="shadow-2">
                                <div className="flex justify-content-between align-items-center mb-3">
                                    <div className="flex align-items-center gap-2">
                                        <i className="pi pi-briefcase text-blue-500"></i>
                                        <h6 className="m-0">Salaried Experience #{index + 1}</h6>
                                    </div>
                                    <Button
                                        icon="pi pi-trash"
                                        className="p-button-rounded p-button-text p-button-danger"
                                        onClick={() => removeSalariedExperience(index)}
                                        tooltip="Remove"
                                        tooltipOptions={{ position: 'top' }}
                                    />
                                </div>

                                <div className="grid">
                                    <div className="col-12 md:col-6">
                                        <label htmlFor={`company_name_${index}`} className="block mb-2 font-medium">Company Name <span className="text-red-500">*</span></label>
                                        <InputText
                                            id={`company_name_${index}`}
                                            name="company_name"
                                            value={work.company_name || ''}
                                            onChange={(e) => updateSalariedExperience(index, 'company_name', e.target.value)}
                                            placeholder="e.g., HDFC Bank"
                                            className="w-full"
                                        />
                                    </div>

                                    <div className="col-12 md:col-6">
                                        <label htmlFor={`job_title_${index}`} className="block mb-2 font-medium">Job Title <span className="text-red-500">*</span></label>
                                        <InputText
                                            id={`job_title_${index}`}
                                            name="job_title"
                                            value={work.job_title || ''}
                                            onChange={(e) => updateSalariedExperience(index, 'job_title', e.target.value)}
                                            placeholder="e.g., Senior Loan Officer"
                                            className="w-full"
                                        />
                                    </div>

                                    <div className="col-12 md:col-6">
                                        <label htmlFor={`employment_type_${index}`} className="block mb-2 font-medium">Employment Type</label>
                                        <Dropdown
                                            id={`employment_type_${index}`}
                                            name="employment_type"
                                            value={work.employment_type || 'full_time'}
                                            options={employmentTypes}
                                            onChange={(e) => updateSalariedExperience(index, 'employment_type', e.value)}
                                            placeholder="Select type"
                                            className="w-full"
                                        />
                                    </div>

                                    <div className="col-12 md:col-6">
                                        <label htmlFor={`location_${index}`} className="block mb-2 font-medium">Location</label>
                                        <InputText
                                            id={`location_${index}`}
                                            name="location"
                                            value={work.location || ''}
                                            onChange={(e) => updateSalariedExperience(index, 'location', e.target.value)}
                                            placeholder="e.g., Mumbai, Maharashtra"
                                            className="w-full"
                                        />
                                    </div>

                                    <div className="col-12 md:col-6">
                                        <label htmlFor={`start_date_${index}`} className="block mb-2 font-medium">Start Date <span className="text-red-500">*</span></label>
                                        <Calendar
                                            id={`start_date_${index}`}
                                            name="start_date"
                                            value={work.start_date ? new Date(work.start_date) : null}
                                            onChange={(e) => updateSalariedExperience(index, 'start_date', e.value)}
                                            dateFormat="dd/mm/yy"
                                            placeholder="Select date"
                                            className="w-full"
                                            maxDate={new Date()}
                                            showIcon
                                            readOnlyInput
                                        />
                                    </div>

                                    <div className="col-12 md:col-6">
                                        <label htmlFor={`end_date_${index}`} className="block mb-2 font-medium">End Date</label>
                                        <Calendar
                                            id={`end_date_${index}`}
                                            name="end_date"
                                            value={work.end_date ? new Date(work.end_date) : null}
                                            onChange={(e) => updateSalariedExperience(index, 'end_date', e.value)}
                                            dateFormat="dd/mm/yy"
                                            placeholder="Select date"
                                            className="w-full"
                                            maxDate={new Date()}
                                            disabled={work.is_current}
                                            showIcon
                                            readOnlyInput
                                        />
                                    </div>

                                    <div className="col-12">
                                        <div className="flex align-items-center gap-2 p-3 bg-primary-50 border-round">
                                            <Checkbox
                                                inputId={`current-salaried-${index}`}
                                                checked={work.is_current}
                                                onChange={(e) => updateSalariedExperience(index, 'is_current', e.checked)}
                                            />
                                            <label htmlFor={`current-salaried-${index}`} className="font-medium">I currently work here</label>
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <label htmlFor={`description_${index}`} className="block mb-2 font-medium">Description</label>
                                        <InputTextarea
                                            id={`description_${index}`}
                                            name="description"
                                            value={work.description || ''}
                                            onChange={(e) => updateSalariedExperience(index, 'description', e.target.value)}
                                            placeholder="Describe your responsibilities, achievements, and key projects..."
                                            rows={4}
                                            className="w-full"
                                        />
                                        <small className="text-500">Include your key responsibilities and achievements</small>
                                    </div>
                                </div>
                            </Card>
                        </div>
                    ))}
                </>
            )}

            {/* Self-Employed Experience Section */}
            {showSelfEmployed && (
                <>
                    <div className="col-12 mt-4">
                        <Divider align="left">
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-user text-green-500"></i>
                                <span className="font-semibold text-lg">Self-Employed / Business</span>
                            </div>
                        </Divider>
                    </div>

                    <div className="col-12">
                        <div className="flex justify-content-between align-items-center mb-3">
                            <p className="text-600 m-0">Add your business or self-employment history</p>
                            <Button
                                label="Add Business Experience"
                                icon="pi pi-plus"
                                onClick={addSelfEmployedExperience}
                                className="p-button-outlined p-button-success p-button-sm"
                            />
                        </div>
                    </div>

                    {selfEmployedExperiences.length === 0 && (
                        <div className="col-12">
                            <div className="surface-100 border-round p-4 text-center">
                                <i className="pi pi-user text-4xl text-400 mb-3"></i>
                                <p className="text-600 m-0">No self-employed experience added yet</p>
                                <p className="text-500 text-sm m-0">Click "Add Business Experience" to get started</p>
                            </div>
                        </div>
                    )}

                    {selfEmployedExperiences.map((business, index) => (
                        <div key={index} className="col-12">
                            <Card className="shadow-2 border-1 border-green-200">
                                <div className="flex justify-content-between align-items-center mb-3">
                                    <div className="flex align-items-center gap-2">
                                        <i className="pi pi-user text-green-500"></i>
                                        <h6 className="m-0">Business/Practice #{index + 1}</h6>
                                    </div>
                                    <Button
                                        icon="pi pi-trash"
                                        className="p-button-rounded p-button-text p-button-danger"
                                        onClick={() => removeSelfEmployedExperience(index)}
                                        tooltip="Remove"
                                        tooltipOptions={{ position: 'top' }}
                                    />
                                </div>

                                <div className="grid">
                                    <div className="col-12 md:col-6">
                                        <label htmlFor={`business_name_${index}`} className="block mb-2 font-medium">Business/Practice Name <span className="text-red-500">*</span></label>
                                        <InputText
                                            id={`business_name_${index}`}
                                            name="business_name"
                                            value={business.business_name || ''}
                                            onChange={(e) => updateSelfEmployedExperience(index, 'business_name', e.target.value)}
                                            placeholder="e.g., Sharma Financial Consultancy"
                                            className="w-full"
                                        />
                                        <small className="text-500">Your business or practice name</small>
                                    </div>

                                    <div className="col-12 md:col-6">
                                        <label htmlFor={`role_title_${index}`} className="block mb-2 font-medium">Role/Title <span className="text-red-500">*</span></label>
                                        <InputText
                                            id={`role_title_${index}`}
                                            name="role_title"
                                            value={business.role_title || ''}
                                            onChange={(e) => updateSelfEmployedExperience(index, 'role_title', e.target.value)}
                                            placeholder="e.g., Founder & Managing Director"
                                            className="w-full"
                                        />
                                        <small className="text-500">Your role in the business</small>
                                    </div>

                                    <div className="col-12 md:col-6">
                                        <label htmlFor={`business_type_${index}`} className="block mb-2 font-medium">Business Type <span className="text-red-500">*</span></label>
                                        <Dropdown
                                            id={`business_type_${index}`}
                                            name="business_type"
                                            value={business.business_type || 'sole_proprietor'}
                                            options={businessTypes}
                                            onChange={(e) => updateSelfEmployedExperience(index, 'business_type', e.value)}
                                            placeholder="Select business type"
                                            className="w-full"
                                        />
                                        <small className="text-500">Legal structure of your business</small>
                                    </div>

                                    <div className="col-12 md:col-6">
                                        <label htmlFor={`business_location_${index}`} className="block mb-2 font-medium">Location</label>
                                        <InputText
                                            id={`business_location_${index}`}
                                            name="business_location"
                                            value={business.location || ''}
                                            onChange={(e) => updateSelfEmployedExperience(index, 'location', e.target.value)}
                                            placeholder="e.g., Pune, Maharashtra"
                                            className="w-full"
                                        />
                                        <small className="text-500">Business location or primary city</small>
                                    </div>

                                    <div className="col-12 md:col-6">
                                        <label htmlFor={`business_start_date_${index}`} className="block mb-2 font-medium">Start Date <span className="text-red-500">*</span></label>
                                        <Calendar
                                            id={`business_start_date_${index}`}
                                            name="business_start_date"
                                            value={business.start_date ? new Date(business.start_date) : null}
                                            onChange={(e) => updateSelfEmployedExperience(index, 'start_date', e.value)}
                                            dateFormat="dd/mm/yy"
                                            placeholder="Select date"
                                            className="w-full"
                                            maxDate={new Date()}
                                            showIcon
                                            readOnlyInput
                                        />
                                        <small className="text-500">When you started this business</small>
                                    </div>

                                    <div className="col-12 md:col-6">
                                        <label htmlFor={`business_end_date_${index}`} className="block mb-2 font-medium">End Date</label>
                                        <Calendar
                                            id={`business_end_date_${index}`}
                                            name="business_end_date"
                                            value={business.end_date ? new Date(business.end_date) : null}
                                            onChange={(e) => updateSelfEmployedExperience(index, 'end_date', e.value)}
                                            dateFormat="dd/mm/yy"
                                            placeholder="Select date"
                                            className="w-full"
                                            maxDate={new Date()}
                                            disabled={business.is_current_running}
                                            showIcon
                                            readOnlyInput
                                        />
                                        <small className="text-500">Leave empty if currently running</small>
                                    </div>

                                    <div className="col-12">
                                        <div className="flex align-items-center gap-2 p-3 bg-green-50 border-round">
                                            <Checkbox
                                                inputId={`current-business-${index}`}
                                                checked={business.is_current_running}
                                                onChange={(e) => updateSelfEmployedExperience(index, 'is_current_running', e.checked)}
                                            />
                                            <label htmlFor={`current-business-${index}`} className="font-medium">Currently Running</label>
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <label htmlFor={`business_description_${index}`} className="block mb-2 font-medium">Description</label>
                                        <InputTextarea
                                            id={`business_description_${index}`}
                                            name="business_description"
                                            value={business.description || ''}
                                            onChange={(e) => updateSelfEmployedExperience(index, 'description', e.target.value)}
                                            placeholder="Describe your business, services offered, key achievements, and notable clients..."
                                            rows={4}
                                            className="w-full"
                                        />
                                        <small className="text-500">Include business nature, services, achievements, and client base</small>
                                    </div>
                                </div>
                            </Card>
                        </div>
                    ))}
                </>
            )}
        </div>
    );
};

export default WorkExperienceStep;
